import { ClipPolygon } from "./clipMask";
import { projectToUtm21S } from "./projection";
import { GeoBounds, RainfallStation } from "./types";

export interface RainfallSurfaceBlock {
  polygonIndexes: number[];
  stations: RainfallStation[];
}

interface MetricPoint {
  x: number;
  y: number;
}

function toMetric(lng: number, lat: number): MetricPoint {
  const projected = projectToUtm21S(lat, lng);
  return {
    x: projected.x / 1000,
    y: projected.y / 1000,
  };
}

function polygonMetricBounds(polygon: ClipPolygon): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  polygon[0].forEach((position) => {
    const point = toMetric(position[0], position[1]);
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  });
  return { minX, minY, maxX, maxY };
}

function boundsDistanceKm(
  a: ReturnType<typeof polygonMetricBounds>,
  b: ReturnType<typeof polygonMetricBounds>,
): number {
  const dx = Math.max(0, a.minX - b.maxX, b.minX - a.maxX);
  const dy = Math.max(0, a.minY - b.maxY, b.minY - a.maxY);
  return Math.sqrt(dx * dx + dy * dy);
}

function pointToSegmentDistance(
  point: MetricPoint,
  start: MetricPoint,
  end: MetricPoint,
): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const ratio = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared,
    ),
  );
  return Math.hypot(
    point.x - (start.x + ratio * dx),
    point.y - (start.y + ratio * dy),
  );
}

function pointInRing(lng: number, lat: number, ring: ClipPolygon[0]): boolean {
  let inside = false;
  for (
    let current = 0, previous = ring.length - 1;
    current < ring.length;
    previous = current++
  ) {
    const currentLng = ring[current][0];
    const currentLat = ring[current][1];
    const previousLng = ring[previous][0];
    const previousLat = ring[previous][1];
    if (
      currentLat > lat !== previousLat > lat &&
      lng <
        ((previousLng - currentLng) * (lat - currentLat)) /
          (previousLat - currentLat) +
          currentLng
    ) {
      inside = !inside;
    }
  }
  return inside;
}

export function pointInClipPolygon(
  lng: number,
  lat: number,
  polygon: ClipPolygon,
): boolean {
  if (!pointInRing(lng, lat, polygon[0])) {
    return false;
  }
  return !polygon.slice(1).some((hole) => pointInRing(lng, lat, hole));
}

function distanceToPolygonKm(
  station: RainfallStation,
  polygon: ClipPolygon,
): number {
  if (pointInClipPolygon(station.lng, station.lat, polygon)) {
    return 0;
  }
  const point = toMetric(station.lng, station.lat);
  let minimum = Number.POSITIVE_INFINITY;
  polygon[0].forEach((position, index, ring) => {
    const next = ring[(index + 1) % ring.length];
    minimum = Math.min(
      minimum,
      pointToSegmentDistance(
        point,
        toMetric(position[0], position[1]),
        toMetric(next[0], next[1]),
      ),
    );
  });
  return minimum;
}

export function buildRainfallSurfaceBlocks(
  polygons: ClipPolygon[],
  stations: RainfallStation[],
  _bounds: GeoBounds,
  blockGapKm = 0.35,
  stationBufferKm = 4,
): RainfallSurfaceBlock[] {
  if (!polygons.length) {
    return [{ polygonIndexes: [], stations }];
  }

  const metricBounds = polygons.map((polygon) => polygonMetricBounds(polygon));
  const parents = polygons.map((_, index) => index);
  const root = (index: number): number => {
    let current = index;
    while (parents[current] !== current) {
      parents[current] = parents[parents[current]];
      current = parents[current];
    }
    return current;
  };
  const join = (a: number, b: number) => {
    const rootA = root(a);
    const rootB = root(b);
    if (rootA !== rootB) {
      parents[rootB] = rootA;
    }
  };

  // Shapely's buffer(gap).buffer(-gap) joins components whose buffers overlap.
  for (let a = 0; a < polygons.length; a += 1) {
    for (let b = a + 1; b < polygons.length; b += 1) {
      if (
        boundsDistanceKm(metricBounds[a], metricBounds[b]) <=
        blockGapKm * 2
      ) {
        join(a, b);
      }
    }
  }

  const grouped = new Map<number, number[]>();
  polygons.forEach((_, index) => {
    const parent = root(index);
    grouped.set(parent, [...(grouped.get(parent) ?? []), index]);
  });

  return Array.from(grouped.values()).map((polygonIndexes: number[]) => {
    const localStations = stations.filter((station) =>
      polygonIndexes.some(
        (polygonIndex) =>
          distanceToPolygonKm(station, polygons[polygonIndex]) <=
          stationBufferKm,
      ),
    );

    return { polygonIndexes, stations: localStations };
  });
}

export function rasterizeBlockIndexes(
  polygons: ClipPolygon[],
  blocks: RainfallSurfaceBlock[],
  bounds: GeoBounds,
  width: number,
  height: number,
): Int16Array {
  const indexes = new Int16Array(width * height);
  indexes.fill(-1);
  const latSpan = bounds.north - bounds.south;
  const lngSpan = bounds.east - bounds.west;

  blocks.forEach((block, blockIndex) => {
    block.polygonIndexes.forEach((polygonIndex) => {
      const polygon = polygons[polygonIndex];
      const outer = polygon[0];
      const lngs = outer.map((point) => point[0]);
      const lats = outer.map((point) => point[1]);
      const minX = Math.max(
        0,
        Math.floor(((Math.min(...lngs) - bounds.west) / lngSpan) * width),
      );
      const maxX = Math.min(
        width - 1,
        Math.ceil(((Math.max(...lngs) - bounds.west) / lngSpan) * width),
      );
      const minY = Math.max(
        0,
        Math.floor(((bounds.north - Math.max(...lats)) / latSpan) * height),
      );
      const maxY = Math.min(
        height - 1,
        Math.ceil(((bounds.north - Math.min(...lats)) / latSpan) * height),
      );

      for (let y = minY; y <= maxY; y += 1) {
        const lat = bounds.north - ((y + 0.5) / height) * latSpan;
        for (let x = minX; x <= maxX; x += 1) {
          const lng = bounds.west + ((x + 0.5) / width) * lngSpan;
          if (pointInClipPolygon(lng, lat, polygon)) {
            indexes[y * width + x] = blockIndex;
          }
        }
      }
    });
  });

  return indexes;
}
